import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const STANDALONE_ROUTES = [
  "/login",
  "/sfc",
  "/sfc/station",
  "/sfc-terminal",
  "/portal",
  "/portal/home",
];

const isStandaloneRoute = (pathname: string) => (
  STANDALONE_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`))
);

const ElectronWindowControls: React.FC = () => {
  const { pathname } = useLocation();
  const [fullscreen, setFullscreen] = useState(Boolean(document.fullscreenElement));

  const electronAPI = (window as any).electronAPI;
  const hasElectronControls = Boolean(
    electronAPI?.isElectron
    && electronAPI?.windowMinimize
    && electronAPI?.windowMaximize
    && electronAPI?.windowClose
  );
  const shouldShow = useMemo(() => isStandaloneRoute(pathname), [pathname]);

  useEffect(() => {
    const onChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  if (!shouldShow) return null;

  const minimizeWindow = () => {
    if (hasElectronControls) {
      electronAPI.windowMinimize();
      return;
    }
    try { window.blur(); } catch (_) {}
  };

  const toggleMaximize = () => {
    if (hasElectronControls) {
      electronAPI.windowMaximize();
      return;
    }
    try {
      if (document.fullscreenElement) document.exitFullscreen();
      else document.documentElement.requestFullscreen?.();
    } catch (_) {}
  };

  const closeWindow = () => {
    if (hasElectronControls) {
      electronAPI.windowClose();
      return;
    }
    try { window.close(); } catch (_) {}
  };

  return (
    <>
      <div className="standalone-window-drag app-drag" onDoubleClick={toggleMaximize} />
      <div className="standalone-window-controls app-no-drag" aria-label="Window controls">
        <button className="win-ctrl-btn app-no-drag" onClick={minimizeWindow} title="Minimize" aria-label="Minimize">
          <svg width="12" height="2" viewBox="0 0 12 2" aria-hidden="true">
            <rect width="12" height="1.5" rx="1" fill="currentColor" />
          </svg>
        </button>

        <button className="win-ctrl-btn app-no-drag" onClick={toggleMaximize} title="Maximize / Restore" aria-label="Maximize or restore">
          {fullscreen ? (
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
              <path d="M4 2.5h6.5v6.5" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
              <rect x="2.5" y="4" width="6.5" height="6.5" rx="1" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <rect x="1.5" y="1.5" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          )}
        </button>

        <button className="win-ctrl-btn win-close app-no-drag" onClick={closeWindow} title="Close" aria-label="Close">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </>
  );
};

export default ElectronWindowControls;
